import React, { useEffect, useRef, useState } from "react";
import {
  MapPin, CalendarDays, Moon, Users, Download, ChevronDown, Plane,
  ArrowRight, Mail, Phone, FileText, CheckCircle2, CreditCard,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────────────────────
   Espíritu Travel · Trip Plan — REDISEÑO EDITORIAL (referencia: Fora Travel)
   Mockup autocontenido para validación. Tema claro/editorial, Cormorant
   Garamond (títulos) + Manrope (texto). Sin dependencias del backend.
   ───────────────────────────────────────────────────────────────────────── */

const ET_LOGO_NAVY = "/brand/et-logo-navy.png";

const IMG = {
  hero: "https://images.unsplash.com/photo-1752886355870-17e8ebe79a05?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  sorrento1: "https://images.unsplash.com/photo-1641924672016-a77e3999a87b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  sorrento2: "https://images.unsplash.com/photo-1652983741794-95907098f75f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  sorrento3: "https://images.unsplash.com/photo-1658409627293-4c9e95fc49cd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  sorrento4: "https://images.unsplash.com/photo-1710898725119-94b41a1b9575?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  trattoria: "https://images.unsplash.com/photo-1669147907102-b7c75a479979?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  capri: "https://images.unsplash.com/photo-1776492174792-b0394ff2c389?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  romeStreet: "https://images.unsplash.com/photo-1753086420381-ece160998cc8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  hotelRoom1: "https://images.unsplash.com/photo-1611892440504-42a792e24d32?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  hotelRoom2: "https://images.unsplash.com/photo-1618773928121-c32242e63f39?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  rome1: "https://images.unsplash.com/photo-1637097057043-658a14e5d26f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  rome2: "https://images.unsplash.com/photo-1676290834313-01599ef657c3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  specialist: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
  cooking: "https://images.unsplash.com/photo-1628169822580-ca53b886cdc9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  colosseum: "https://images.unsplash.com/photo-1704915332184-68202025c9ba?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  vatican: "https://images.unsplash.com/photo-1694254058667-dbe82ce1ac7b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  florence: "https://images.unsplash.com/photo-1558768059-08c06ad515b7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  beachclub: "https://images.unsplash.com/photo-1576540882014-86499436546b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
};

const eur = (n) => new Intl.NumberFormat("en-IE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n) || 0);

/* ── Datos de ejemplo (mapeables 1:1 al modelo real de SOFI) ─────────────── */
const TRIP = {
  title: "Casselle Family Italy Vacation",
  dateRange: "Jul 9 – 18, 2026",
  location: "Sorrento & Rome",
  travelers: 4,
  nights: 9,
  hero: IMG.hero,
  intro:
    "Ten days weaving together the sun-drenched coastline of Sorrento and the timeless grandeur of Rome. Slow mornings by the sea, a private cooking class on Capri, first-entry access to the Colosseum, and dinners chosen for their soul rather than their address.",
  specialist: {
    name: "Beatriz de Pablo",
    role: "Your Travel Designer",
    email: "beatriz@espiritutravel.com",
    phone: "+34 600 123 456",
    photo: IMG.specialist,
  },
  price: { total: 18450, deposit: 5535 },
};

const DAYS = [
  {
    id: "jul-9", nav: "Jul 9", weekday: "Thursday", date: "July 9",
    items: [
      { kind: "event", status: "Confirmed", title: "Transfer from Naples Airport to Ara Maris",
        rows: [["Meeting Point", "Arrivals Area"], ["Meeting Time", "8:10 am"], ["Duration", "1 hour"]],
        desc: "Your driver will meet you in the arrivals area of Naples Airport before transporting you to Ara Maris ahead of check-in. Welcome to Italy!" },
      { kind: "hotel", name: "Ara Maris", dateRange: "Thu, Jul 9 — Mon, Jul 13, 2026", nights: 4,
        images: [IMG.sorrento1, IMG.sorrento2, IMG.sorrento3, IMG.sorrento4],
        address: "Via Correale 15, Sorrento, Campania, 80067, Italy",
        mapUrl: "https://www.google.com/maps/search/?api=1&query=Ara+Maris+Sorrento",
        rooms: ["1 × Deluxe Room — 30 m²", "1 × Superior Room — 26 m²"],
        perks: ["Upgrade on arrival, subject to availability", "Daily breakfast for up to two guests per bedroom", "$100 hotel credit per stay", "Early check-in / late check-out, subject to availability"],
        cancellation: ["Cancel by midnight hotel time on June 18th, 2026 to avoid paying for the full stay.", "Cancellations 22+ days before arrival have no penalty."] },
      { kind: "event", status: "Recommended", title: "Day at Leisure in Sorrento", image: IMG.sorrento2,
        desc: "As this is your first day, we suggest settling into the hotel and wandering the lemon-scented streets of Sorrento at your own pace." },
      { kind: "event", status: "Confirmed", title: "Dinner at L'Antica Trattoria", image: IMG.trattoria,
        rows: [["Reservation", "Kevin Casselle"], ["Time", "7:00 pm"]],
        desc: "Tucked into the winding alleys of the historic center, L'Antica Trattoria is a masterclass in Italian hospitality. Dine beneath a pergola of vines and flowers, with citrus-infused dishes that lean on Sorrento's volcanic soil and the daily catch from the Tyrrhenian Sea." },
    ],
  },
  {
    id: "jul-10", nav: "Jul 10", weekday: "Friday", date: "July 10",
    items: [
      { kind: "event", status: "Confirmed", title: "Ferry to Capri",
        rows: [["Departure", "8:20 am"], ["Duration", "22 minutes"]],
        desc: "Catch one of the first ferries to explore the island before the crowds arrive and ahead of your cooking class." },
      { kind: "event", status: "Recommended", title: "Walk to the Faraglioni Rocks", image: IMG.capri,
        desc: "Follow Via Tragara toward Punta Tragara and the iconic Faraglioni Rocks — best done first thing, with minimal crowds. Stop for a drink at the Tragara Club for one of the finest views on the island." },
      { kind: "event", status: "Confirmed", title: "Private Cooking Class at Giardino di Capri", image: IMG.cooking,
        rows: [["Meeting Point", "Piazza Europa, Via Roma"], ["Meeting Time", "1:30 pm"], ["Duration", "3.5 hours"]],
        desc: "Learn to cook a traditional Caprese three-course meal from a local chef using homegrown produce and recipes passed down through generations. Afterwards, you'll be driven directly to the port for your return ferry." },
    ],
  },
  {
    id: "jul-11", nav: "Jul 11", weekday: "Saturday", date: "July 11",
    items: [
      { kind: "event", status: "Confirmed", title: "Day at Marameo Beach Club", image: IMG.beachclub,
        rows: [["Booking", "Casselle"], ["Arrival", "11:00 am"], ["Price", "€220 paid directly"]],
        desc: "A relaxed day by the water with beach towels, a welcome prosecco, complimentary golf-cart transport and a 10% discount on food & beverage. Collect your wristbands from the concierge in the morning." },
      { kind: "event", status: "Confirmed", title: "Dinner at Bagni Delfino", image: IMG.sorrento1,
        rows: [["Reservation", "Kevin Casselle"], ["Time", "7:00 pm"]],
        desc: "Built out on a pier in the fishing village of Marina Grande, Bagni Delfino lets you dine directly over the Mediterranean. A pure love letter to the local catch — sea bass, peppery mussels and their renowned lobster pasta." },
    ],
  },
  {
    id: "jul-12", nav: "Jul 12", weekday: "Sunday", date: "July 12",
    items: [
      { kind: "note", title: "A slower Sunday", text: "Keep today unhurried — the hotel pool, a long lunch, and a final Sorrento sunset before heading to Rome tomorrow." },
      { kind: "event", status: "Confirmed", title: "Dinner at La Pergola", image: IMG.sorrento3,
        rows: [["Reservation", "Ben Murphy"], ["Time", "7:00 pm"], ["Dress", "Smart casual"]],
        desc: "A refined evening at Bellevue Syrene overlooking the Bay of Naples. Note a minimum spend of €80 per person and a strict 24-hour cancellation policy." },
    ],
  },
  {
    id: "jul-13", nav: "Jul 13", weekday: "Monday", date: "July 13",
    items: [
      { kind: "event", status: "Confirmed", title: "Transfer to Naples Train Station",
        rows: [["Meeting Point", "Hotel Lobby"], ["Meeting Time", "10:30 am"], ["Duration", "1.5 hours"]] },
      { kind: "event", status: "Confirmed", title: "Train from Naples to Roma Termini",
        rows: [["Departure", "1:55 pm"], ["Duration", "1 hour"]] },
      { kind: "hotel", name: "Hotel Chapter Roma", dateRange: "Mon, Jul 13 — Sat, Jul 18, 2026", nights: 5,
        images: [IMG.hotelRoom1, IMG.rome1, IMG.hotelRoom2, IMG.rome2],
        address: "47 Via Santa Maria, Rome, Lazio, 00186, Italy",
        mapUrl: "https://www.google.com/maps/search/?api=1&query=Chapter+Roma+Rome+Italy",
        rooms: ["2 × Standard Double King — courtyard or city view, 22 m²"],
        pricing: { lines: [["1 room × 5 nights", 1418], ["Taxes & fees (incl. 10% VAT)", 142]], total: 1560, dueBooking: 0, dueProperty: 1560, note: "Rate above is per room." },
        perks: ["Complimentary room upgrade, subject to availability", "Welcome flute of prosecco on arrival", "€50 food & beverage credit per room"],
        cancellation: ["Cancel by 3:00 pm hotel time on July 12th, 2026 to avoid a one-night charge.", "Cancellations under 24 hours incur a one-night fee, including taxes."] },
      { kind: "event", status: "Confirmed", title: "Golf Cart Tour of Rome", image: IMG.romeStreet,
        rows: [["Meeting Point", "Chapter Roma"], ["Meeting Time", "8:00 pm"], ["Duration", "2 hours"]],
        desc: "A magical way to see the Eternal City by night — the fountains, piazzas and monuments lit up, without the crowds." },
    ],
  },
  {
    id: "jul-14", nav: "Jul 14", weekday: "Tuesday", date: "July 14",
    items: [
      { kind: "event", status: "Confirmed", title: "First-Entry Private Colosseum & Roman Forum Tour", image: IMG.colosseum,
        rows: [["Meeting Point", "Largo Corrado Ricci, 40"], ["Meeting Time", "9:00 am"], ["Duration", "3 hours"], ["Price", "€650"]],
        desc: "Beat the midday crowds with first-entry access, walking through the Gladiator's Gate for exclusive Arena Floor access. Your expert guide continues through the Roman Forum and Palatine Hill." },
      { kind: "event", status: "Confirmed", title: "Dinner at Les Etoiles", image: IMG.rome1,
        rows: [["Reservation", "Ben Murphy"], ["Time", "7:30 pm"], ["Code", "BCKCYG9Y"]],
        desc: "Perched atop the Hotel Atlante Star with a direct, unobstructed view of St. Peter's Basilica. A sophisticated take on Mediterranean cuisine as the sky turns to gold at sunset." },
    ],
  },
  {
    id: "jul-15", nav: "Jul 15", weekday: "Wednesday", date: "July 15",
    items: [
      { kind: "event", status: "Confirmed", title: "Vatican Highlights, Sistine Chapel & St. Peter's Basilica", image: IMG.vatican,
        rows: [["Meeting Point", "Viale Vaticano 100"], ["Meeting Time", "2:00 pm"]],
        desc: "A guided journey through the Vatican's most celebrated masterpieces, culminating in the Sistine Chapel and the breathtaking St. Peter's Basilica." },
      { kind: "event", status: "Recommended", title: "Flexible Morning in Rome",
        desc: "Keep the morning open — a coffee in Piazza Navona, a stroll through the Pantheon, or simply time to wander." },
    ],
  },
  {
    id: "jul-16", nav: "Jul 16", weekday: "Thursday", date: "July 16",
    items: [
      { kind: "event", status: "Confirmed", title: "Florence Day Trip — Train from Roma Termini", image: IMG.florence,
        rows: [["Departure", "8:10 am"], ["Duration", "1h 15m"]],
        desc: "A high-speed hop to Florence for a day among Renaissance treasures. Return train departs Florence at 9:14 pm." },
    ],
  },
  {
    id: "jul-17", nav: "Jul 17", weekday: "Friday", date: "July 17",
    items: [
      { kind: "event", status: "Confirmed", title: "Dinner at Trattoria der Pallaro", image: IMG.rome2,
        rows: [["Reservation", "Ben Murphy"], ["Time", "7:00 pm"]],
        desc: "Run by the same family since 1955, just off Campo de' Fiori. There's no menu — you choose the wine and water, and proper Roman home cooking arrives course by course." },
    ],
  },
  {
    id: "jul-18", nav: "Jul 18", weekday: "Saturday", date: "July 18",
    items: [
      { kind: "event", status: "Confirmed", title: "Transfer from Chapter Roma to Rome FCO Airport",
        rows: [["Meeting Point", "Hotel Lobby"], ["Meeting Time", "8:30 am"], ["Duration", "45 minutes"]] },
      { kind: "flight", airline: "Delta Air Lines", flightNo: "DL231", status: "Confirmed",
        from: { code: "FCO", city: "Rome, Italy", time: "12:15 PM", date: "Sat, Jul 18" },
        to: { code: "JFK", city: "New York, NY", time: "4:05 PM", date: "Sat, Jul 18" },
        duration: "9h 50m" },
    ],
  },
];

const DOCUMENTS = [
  { name: "Ferry Tickets — Sorrento to Capri", size: "1.2 MB" },
  { name: "Ferry Tickets — Capri to Sorrento", size: "166 KB" },
  { name: "Train — Naples to Roma Termini", size: "580 KB" },
  { name: "Train — Rome to Florence (return)", size: "290 KB" },
];

/* ── Componentes ─────────────────────────────────────────────────────────── */

const StyleTag = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=Manrope:wght@300;400;500;600;700&display=swap');
    .tp-serif{font-family:'Cormorant Garamond',Georgia,serif;}
    .tp-sans{font-family:'Manrope',system-ui,-apple-system,sans-serif;}
    .tp-noscroll::-webkit-scrollbar{display:none}
    .tp-noscroll{-ms-overflow-style:none;scrollbar-width:none}
    html{scroll-behavior:smooth}
  `}</style>
);

const Overline = ({ children, className = "" }) => (
  <p className={`tp-sans text-[11px] font-semibold uppercase tracking-[0.22em] text-[#71717A] ${className}`}>{children}</p>
);

const EventItem = ({ item, testid }) => (
  <div className="flex flex-col gap-6 border-b border-[#E4E4E7] py-10 last:border-b-0 md:flex-row md:gap-12" data-testid={testid}>
    <div className="w-full flex-shrink-0 md:w-44">
      <Overline className={item.status === "Recommended" ? "text-[#A16207]" : "text-[#3F6212]"}>{item.status}</Overline>
      {item.rows?.[0]?.[0] === "Meeting Time" || item.rows?.some((r) => /time|departure/i.test(r[0])) ? (
        <p className="tp-serif mt-2 text-2xl text-[#1A1A1A]">
          {(item.rows.find((r) => /time|departure/i.test(r[0])) || [])[1]}
        </p>
      ) : null}
    </div>
    <div className="min-w-0 flex-1">
      <h3 className="tp-serif text-2xl font-medium leading-snug text-[#1A1A1A] md:text-3xl">{item.title}</h3>
      {item.rows?.length ? (
        <dl className="mt-3 grid grid-cols-1 gap-x-8 gap-y-1 sm:grid-cols-2">
          {item.rows.map(([k, v], i) => (
            <div key={i} className="flex gap-2 text-sm">
              <dt className="tp-sans font-semibold text-[#52525B]">{k}:</dt>
              <dd className="tp-sans font-light text-[#1A1A1A]">{v}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {item.desc ? <p className="tp-sans mt-4 text-base font-light leading-relaxed text-[#2C2C2C] md:text-lg">{item.desc}</p> : null}
      {item.image ? (
        <div className="mt-6 overflow-hidden rounded-sm">
          <img src={item.image} alt={item.title} loading="lazy" className="h-64 w-full object-cover transition-transform duration-[1200ms] hover:scale-105" />
        </div>
      ) : null}
    </div>
  </div>
);

const NoteItem = ({ item, testid }) => (
  <div className="border-b border-[#E4E4E7] py-10 last:border-b-0" data-testid={testid}>
    <Overline className="text-[#71717A]">A note from your designer</Overline>
    <h3 className="tp-serif mt-2 text-2xl font-medium text-[#1A1A1A] md:text-3xl">{item.title}</h3>
    <p className="tp-sans mt-3 text-base font-light italic leading-relaxed text-[#52525B] md:text-lg">{item.text}</p>
  </div>
);

const HotelBlock = ({ item, testid }) => (
  <div className="my-10 border border-[#E4E4E7] bg-white p-6 md:p-10" data-testid={testid}>
    <Overline className="text-[#71717A]">Where you stay</Overline>
    <div className="mt-2 flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
      <h3 className="tp-serif text-3xl font-medium text-[#1A1A1A] md:text-4xl">{item.name}</h3>
      <p className="tp-sans flex items-center gap-2 text-sm font-light text-[#52525B]">
        <Moon className="h-3.5 w-3.5" /> {item.dateRange} · {item.nights} nights
      </p>
    </div>
    {item.images?.length ? (
      <div className="mt-6 grid grid-cols-2 gap-2">
        {item.images.slice(0, 4).map((u, i) => (
          <div key={i} className="overflow-hidden rounded-sm">
            <img src={u} alt={item.name} loading="lazy" className="h-40 w-full object-cover transition-transform duration-[1200ms] hover:scale-105 md:h-52" />
          </div>
        ))}
      </div>
    ) : null}
    <div className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-2">
      <div className="space-y-6">
        <div>
          <Overline>Address</Overline>
          <a href={item.mapUrl} target="_blank" rel="noreferrer" data-testid={`${testid}-map`}
            className="tp-sans mt-1.5 inline-flex items-start gap-1.5 text-sm font-light text-[#1A1A1A] underline decoration-[#D4D4D8] underline-offset-4 hover:decoration-[#1A1A1A]">
            <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#A1A1AA]" /> {item.address}
          </a>
        </div>
        <div>
          <Overline>Your rooms</Overline>
          <ul className="tp-sans mt-1.5 space-y-1 text-sm font-light text-[#1A1A1A]">
            {item.rooms.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
        {item.pricing ? (
          <div>
            <Overline>Rate</Overline>
            <table className="tp-sans mt-1.5 w-full text-sm font-light text-[#1A1A1A]">
              <tbody>
                {item.pricing.lines.map(([k, v], i) => (
                  <tr key={i}><td className="py-1 text-[#52525B]">{k}</td><td className="py-1 text-right">{eur(v)}</td></tr>
                ))}
                <tr className="border-t border-[#D4D4D8]"><td className="py-1.5 font-semibold">Total</td><td className="py-1.5 text-right font-semibold">{eur(item.pricing.total)}</td></tr>
              </tbody>
            </table>
            <p className="tp-sans mt-2 text-xs font-light italic text-[#A1A1AA]">{item.pricing.note}</p>
          </div>
        ) : null}
      </div>
      <div className="space-y-6">
        <div>
          <Overline>As clients of Espíritu Travel, you enjoy</Overline>
          <ul className="tp-sans mt-1.5 space-y-1.5 text-sm font-light text-[#1A1A1A]">
            {item.perks.map((p, i) => (
              <li key={i} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#3F6212]" />{p}</li>
            ))}
          </ul>
        </div>
        <div>
          <Overline>Cancellation policy</Overline>
          <ul className="tp-sans mt-1.5 list-disc space-y-1 pl-4 text-sm font-light text-[#52525B]">
            {item.cancellation.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </div>
      </div>
    </div>
  </div>
);

const FlightCard = ({ item, testid }) => (
  <div className="my-10 border border-[#E4E4E7] bg-[#F9F8F6] p-6 md:p-8" data-testid={testid}>
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#121B28]"><Plane className="h-4 w-4 text-white" /></div>
      <div>
        <p className="tp-sans text-sm font-semibold text-[#1A1A1A]">{item.airline}</p>
        <p className="tp-sans text-xs font-light text-[#71717A]">Flight {item.flightNo} · {item.status}</p>
      </div>
    </div>
    <div className="mt-6 flex items-center justify-between gap-4">
      <div className="text-left">
        <p className="tp-serif text-4xl font-medium text-[#1A1A1A] md:text-5xl">{item.from.code}</p>
        <p className="tp-sans mt-1 text-xs font-light text-[#52525B]">{item.from.city}</p>
        <p className="tp-sans text-sm font-medium text-[#1A1A1A]">{item.from.time}</p>
        <p className="tp-sans text-xs font-light text-[#A1A1AA]">{item.from.date}</p>
      </div>
      <div className="flex flex-1 flex-col items-center px-2">
        <p className="tp-sans text-xs font-light text-[#A1A1AA]">{item.duration}</p>
        <div className="relative my-1 h-px w-full bg-[#D4D4D8]"><Plane className="absolute -top-2 left-1/2 h-4 w-4 -translate-x-1/2 rotate-90 text-[#71717A]" /></div>
        <p className="tp-sans text-[11px] font-light uppercase tracking-wider text-[#A1A1AA]">Nonstop</p>
      </div>
      <div className="text-right">
        <p className="tp-serif text-4xl font-medium text-[#1A1A1A] md:text-5xl">{item.to.code}</p>
        <p className="tp-sans mt-1 text-xs font-light text-[#52525B]">{item.to.city}</p>
        <p className="tp-sans text-sm font-medium text-[#1A1A1A]">{item.to.time}</p>
        <p className="tp-sans text-xs font-light text-[#A1A1AA]">{item.to.date}</p>
      </div>
    </div>
  </div>
);

export default function TripPlanFora() {
  const [active, setActive] = useState(DAYS[0].id);
  const [payState, setPayState] = useState("idle");
  const navRef = useRef(null);
  const sectionRefs = useRef({});

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: [0, 0.25, 0.5, 1] }
    );
    Object.values(sectionRefs.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.offsetTop - 88, behavior: "smooth" });
  };

  const navItems = [...DAYS.map((d) => ({ id: d.id, label: d.nav })), { id: "documents", label: "Documents" }];

  return (
    <div className="tp-sans min-h-screen bg-[#F9F8F6] text-[#1A1A1A]" data-testid="trip-plan-fora">
      <StyleTag />

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-[#E4E4E7] bg-white/90 backdrop-blur-md" data-testid="tp-header">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-5 py-3.5 md:px-8">
          <div className="flex items-center gap-3">
            <div className="flex h-9 items-center rounded-md border border-[#E4E4E7] bg-white px-3">
              <img src={ET_LOGO_NAVY} alt="Espíritu Travel" className="h-4 w-auto" data-testid="tp-logo" />
            </div>
          </div>
          <nav ref={navRef} className="tp-noscroll mx-2 flex flex-1 items-center gap-4 overflow-x-auto md:gap-6" data-testid="tp-daynav">
            {navItems.map((n) => (
              <button key={n.id} onClick={() => scrollTo(n.id)} data-testid={`tp-nav-${n.id}`}
                className={`tp-sans whitespace-nowrap text-sm transition-colors ${active === n.id ? "font-semibold text-[#1A1A1A]" : "font-light text-[#A1A1AA] hover:text-[#52525B]"}`}>
                {n.label}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-2.5">
            <button onClick={() => scrollTo("reserve")} data-testid="tp-header-reserve"
              className="tp-sans flex items-center gap-1.5 whitespace-nowrap rounded-full bg-[#121B28] px-4 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90">
              <CheckCircle2 className="h-3.5 w-3.5" /> Reserve · {eur(TRIP.price.deposit)}
            </button>
            <button onClick={() => window.print()} data-testid="tp-download"
              className="tp-sans hidden items-center gap-1.5 whitespace-nowrap text-sm font-medium text-[#1A1A1A] hover:opacity-70 md:flex">
              <Download className="h-4 w-4" /> Download PDF
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative flex h-[86vh] min-h-[520px] w-full items-end overflow-hidden" data-testid="tp-hero">
        <img src={TRIP.hero} alt={TRIP.title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/10" />
        {/* elemento decorativo vertical */}
        <div className="absolute left-6 top-1/2 hidden -translate-y-1/2 flex-col gap-2 md:flex">
          {Array.from({ length: 14 }).map((_, i) => (
            <span key={i} className="block h-px bg-white/40" style={{ width: i % 4 === 0 ? 22 : 12 }} />
          ))}
        </div>
        {/* specialist chip */}
        <div className="absolute right-5 top-5 flex items-center gap-3 rounded-full border border-white/20 bg-white/10 py-1.5 pl-1.5 pr-4 backdrop-blur-md md:right-8 md:top-8" data-testid="tp-specialist-chip">
          <img src={TRIP.specialist.photo} alt={TRIP.specialist.name} className="h-9 w-9 rounded-full object-cover ring-2 ring-white/50" />
          <div className="leading-tight">
            <p className="tp-sans text-[9px] font-semibold uppercase tracking-[0.18em] text-white/70">Your travel designer</p>
            <p className="tp-sans text-xs font-semibold text-white">{TRIP.specialist.name}</p>
          </div>
        </div>
        <div className="relative mx-auto w-full max-w-5xl px-5 pb-16 md:px-8">
          <p className="tp-sans mb-3 flex items-center gap-2 text-sm font-light text-white/90" data-testid="tp-hero-location">
            <MapPin className="h-4 w-4" /> {TRIP.location}
          </p>
          <h1 className="tp-serif max-w-3xl text-5xl font-medium leading-[1.05] tracking-tight text-white md:text-7xl" data-testid="tp-hero-title">{TRIP.title}</h1>
          <p className="tp-sans mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-light text-white/90 md:text-base">
            <span className="flex items-center gap-2"><CalendarDays className="h-4 w-4" /> {TRIP.dateRange}</span>
            <span className="flex items-center gap-2"><Moon className="h-4 w-4" /> {TRIP.nights} nights</span>
            <span className="flex items-center gap-2"><Users className="h-4 w-4" /> {TRIP.travelers} travellers</span>
          </p>
        </div>
        <div className="absolute bottom-6 left-1/2 flex -translate-x-1/2 flex-col items-center gap-1 text-white/70">
          <span className="tp-sans text-[11px] uppercase tracking-[0.2em]">Scroll to view</span>
          <ChevronDown className="h-4 w-4 animate-bounce" />
        </div>
      </section>

      {/* Intro */}
      <section className="mx-auto max-w-3xl px-5 py-20 text-center md:px-8" data-testid="tp-intro">
        <Overline>Your journey ahead</Overline>
        <p className="tp-serif mt-4 text-2xl font-light leading-relaxed text-[#1A1A1A] md:text-3xl">{TRIP.intro}</p>
      </section>

      {/* Days */}
      <main className="mx-auto max-w-4xl px-5 md:px-12">
        {DAYS.map((d) => (
          <section key={d.id} id={d.id} ref={(el) => (sectionRefs.current[d.id] = el)}
            className="border-t border-[#E4E4E7] pb-6 pt-20 first:border-t-0" data-testid={`tp-day-${d.id}`}>
            <div className="mb-2 flex items-baseline gap-3">
              <Overline>{d.weekday}</Overline>
            </div>
            <h2 className="tp-serif text-4xl font-medium tracking-tight text-[#1A1A1A] md:text-5xl">{d.date}</h2>
            <div className="mt-6">
              {d.items.map((item, i) => {
                const tid = `tp-day-${d.id}-item-${i}`;
                if (item.kind === "hotel") return <HotelBlock key={i} item={item} testid={tid} />;
                if (item.kind === "flight") return <FlightCard key={i} item={item} testid={tid} />;
                if (item.kind === "note") return <NoteItem key={i} item={item} testid={tid} />;
                return <EventItem key={i} item={item} testid={tid} />;
              })}
            </div>
          </section>
        ))}

        {/* Documents */}
        <section id="documents" ref={(el) => (sectionRefs.current["documents"] = el)}
          className="border-t border-[#E4E4E7] pb-16 pt-20" data-testid="tp-documents">
          <Overline>Everything in one place</Overline>
          <h2 className="tp-serif mt-2 text-4xl font-medium tracking-tight text-[#1A1A1A] md:text-5xl">Documents</h2>
          <div className="mt-8 grid grid-cols-1 gap-3 md:grid-cols-2">
            {DOCUMENTS.map((doc, i) => (
              <a key={i} href="#" onClick={(e) => e.preventDefault()} data-testid={`tp-doc-${i}`}
                className="flex items-center gap-4 border border-[#E4E4E7] bg-white p-4 transition-colors hover:bg-[#F4F4F5]">
                <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-sm bg-[#F4F4F5]"><FileText className="h-5 w-5 text-[#52525B]" /></div>
                <div className="min-w-0 flex-1">
                  <p className="tp-sans truncate text-sm font-medium text-[#1A1A1A]">{doc.name}</p>
                  <p className="tp-sans text-xs font-light text-[#A1A1AA]">{doc.size}</p>
                </div>
                <Download className="h-4 w-4 flex-shrink-0 text-[#A1A1AA]" />
              </a>
            ))}
          </div>
        </section>
      </main>

      {/* Travel specialist */}
      <section className="mx-auto max-w-4xl px-5 pb-8 md:px-12" data-testid="tp-specialist">
        <div className="flex flex-col items-center gap-6 border border-[#E4E4E7] bg-white p-8 text-center md:flex-row md:gap-10 md:p-10 md:text-left">
          <img src={TRIP.specialist.photo} alt={TRIP.specialist.name} className="h-24 w-24 rounded-full object-cover ring-1 ring-[#E4E4E7]" />
          <div className="flex-1">
            <Overline>Designed for you by</Overline>
            <h3 className="tp-serif mt-1 text-3xl font-medium text-[#1A1A1A]">{TRIP.specialist.name}</h3>
            <p className="tp-sans text-sm font-light text-[#52525B]">{TRIP.specialist.role}</p>
            <div className="mt-4 flex flex-col items-center gap-3 sm:flex-row md:justify-start">
              <a href={`mailto:${TRIP.specialist.email}`} className="tp-sans flex items-center gap-2 text-sm font-light text-[#1A1A1A] hover:opacity-70"><Mail className="h-4 w-4 text-[#A1A1AA]" />{TRIP.specialist.email}</a>
              <a href={`tel:${TRIP.specialist.phone}`} className="tp-sans flex items-center gap-2 text-sm font-light text-[#1A1A1A] hover:opacity-70"><Phone className="h-4 w-4 text-[#A1A1AA]" />{TRIP.specialist.phone}</a>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing / Reserve & Pay */}
      <section id="reserve" className="mt-8 bg-[#121B28] px-5 py-20 text-center text-white" data-testid="tp-reserve">
        <div className="mx-auto max-w-xl">
          {payState === "booked" ? (
            <div data-testid="tp-booked">
              <CheckCircle2 className="mx-auto mb-4 h-14 w-14 text-[#8a9771]" />
              <h2 className="tp-serif text-4xl font-medium md:text-5xl">Booked — you're going!</h2>
              <p className="tp-sans mx-auto mt-4 max-w-md text-sm font-light leading-relaxed text-white/80">
                Thank you! We've received your deposit of {eur(TRIP.price.deposit)}. Your trip is confirmed — the remaining {eur(TRIP.price.total - TRIP.price.deposit)} is automatically scheduled about 45 days before departure.
              </p>
              <button data-testid="tp-receipt-btn"
                className="tp-sans mx-auto mt-6 inline-flex items-center gap-2 bg-white px-6 py-3 text-sm font-semibold text-[#121B28] transition-colors hover:bg-[#F4EBD7]">
                <Download className="h-4 w-4" /> Download receipt
              </button>
            </div>
          ) : (
            <>
              <Overline className="!text-white/60">Total Price</Overline>
              <p className="tp-serif mt-3 text-6xl font-medium md:text-7xl" data-testid="tp-total-price">{eur(TRIP.price.total)}</p>
              <p className="tp-sans mt-2 text-sm font-light text-white/70">total for your group · {TRIP.travelers} travellers</p>
              <div className="mx-auto mt-8 max-w-sm border border-white/15 p-6 text-left">
                <p className="tp-sans text-[11px] font-semibold uppercase tracking-[0.2em] text-white/60">Due now · 30% deposit</p>
                <p className="tp-serif mt-1 text-4xl font-medium">{eur(TRIP.price.deposit)}</p>
                <p className="tp-sans mt-2 text-xs font-light leading-relaxed text-white/70">Reserve your dates today; the remaining {eur(TRIP.price.total - TRIP.price.deposit)} is due 45 days before departure.</p>
                {payState === "idle" ? (
                  <button onClick={() => setPayState("pay")} data-testid="tp-book-btn"
                    className="tp-sans mt-5 flex w-full items-center justify-center gap-2 bg-white px-6 py-3.5 text-sm font-semibold text-[#121B28] transition-colors hover:bg-[#F4EBD7]">
                    Book this trip · pay {eur(TRIP.price.deposit)} <ArrowRight className="h-4 w-4" />
                  </button>
                ) : (
                  <div className="mt-5 space-y-2.5" data-testid="tp-paypal">
                    <button onClick={() => setPayState("booked")} data-testid="tp-pay-paypal"
                      className="flex w-full items-center justify-center rounded-full bg-[#FFC439] px-6 py-3 text-base transition-opacity hover:opacity-90">
                      <span className="font-extrabold italic tracking-tight text-[#003087]">Pay<span className="text-[#0070E0]">Pal</span></span>
                    </button>
                    <button onClick={() => setPayState("booked")} data-testid="tp-pay-card"
                      className="tp-sans flex w-full items-center justify-center gap-2 rounded-full bg-black px-6 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90">
                      <CreditCard className="h-4 w-4" /> Debit or Credit Card
                    </button>
                    <button onClick={() => setPayState("idle")} className="tp-sans w-full pt-1 text-xs font-light text-white/50 hover:text-white/80">← Back</button>
                  </div>
                )}
              </div>
              <p className="tp-sans mt-4 text-[11px] font-light text-white/40">Secure payment · powered by PayPal</p>
              <button data-testid="tp-approve-btn"
                className="tp-sans mt-4 block w-full text-sm font-light text-white/70 underline decoration-white/30 underline-offset-4 hover:text-white">
                Prefer to approve first? Approve this proposal
              </button>
            </>
          )}
          {/* Ayuda de mockup: previsualizar estados de pago */}
          <div className="mx-auto mt-12 flex w-fit items-center gap-1 rounded-full border border-white/10 bg-white/5 p-1" data-testid="tp-preview-states">
            {[["idle", "Reserve"], ["pay", "Payment"], ["booked", "Booked"]].map(([k, l]) => (
              <button key={k} onClick={() => setPayState(k)}
                className={`tp-sans rounded-full px-3.5 py-1 text-[11px] transition-colors ${payState === k ? "bg-white font-semibold text-[#121B28]" : "text-white/50 hover:text-white"}`}>{l}</button>
            ))}
          </div>
          <p className="tp-sans mt-2 text-[10px] font-light uppercase tracking-[0.2em] text-white/30">Mockup · preview payment states</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#121B28] px-5 py-16 text-center text-white/80" data-testid="tp-footer">
        <div className="mx-auto max-w-3xl border-t border-white/10 pt-12">
          <p className="tp-serif text-3xl font-light italic text-white">Feel part of the world</p>
          <div className="mt-8 flex flex-col items-center gap-3">
            <span className="tp-sans text-[11px] uppercase tracking-[0.3em] text-white/40">Crafted by</span>
            <img src="/brand/et-logo-sand.png" alt="Espíritu Travel" className="h-11 w-auto" data-testid="tp-footer-logo" />
          </div>
        </div>
      </footer>
    </div>
  );
}
